import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from '../components/CTAButton';
import { SearchIcon } from 'lucide-react';
export function HeroSection() {
  return <section className="relative pt-12 pb-8 md:pt-16 md:pb-12 px-6 overflow-hidden bg-[#FFF0F5] min-h-[600px] md:min-h-[700px]" data-id="element-218">
      {/* Background: Woman drinking coffee, worried expression */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" data-id="element-219">
        <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=2070&auto=format&fit=crop" alt="Mujer pensativa con café" className="w-full h-full object-cover object-top scale-110" data-id="element-220" />
        {/* Gradient overlays for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFF0F5]/90 via-[#FFF0F5]/80 to-[#FFF0F5]/60" data-id="element-221" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFF0F5] via-[#FFF0F5]/40 to-transparent" data-id="element-222" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFF0F5]/70 via-transparent to-[#FFF0F5]/90" data-id="element-223" />

        {/* Animated Steam / Smoke wisps */}
        <motion.div className="absolute bottom-[20%] right-[15%] w-16 h-40 bg-white/50 blur-2xl rounded-full" animate={{
        y: [0, -120, -240],
        x: [0, 15, -8],
        opacity: [0, 0.6, 0],
        scale: [0.8, 1.4, 2.2]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeOut'
      }} data-id="element-224" />
        <motion.div className="absolute bottom-[15%] right-[20%] w-12 h-32 bg-white/40 blur-xl rounded-full" animate={{
        y: [0, -100, -200],
        x: [0, -12, 8],
        opacity: [0, 0.5, 0],
        scale: [0.6, 1.2, 1.8]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: 'easeOut',
        delay: 1.5
      }} data-id="element-225" />
        <motion.div className="absolute bottom-[25%] right-[18%] w-20 h-48 bg-white/35 blur-3xl rounded-full" animate={{
        y: [0, -160, -300],
        x: [0, 25, -15],
        opacity: [0, 0.45, 0],
        scale: [1, 1.8, 2.8]
      }} transition={{
        duration: 9,
        repeat: Infinity,
        ease: 'easeOut',
        delay: 3
      }} data-id="element-226" />
        <motion.div className="absolute bottom-[18%] right-[22%] w-10 h-28 bg-white/45 blur-xl rounded-full" animate={{
        y: [0, -80, -180],
        x: [0, -8, 12],
        opacity: [0, 0.55, 0],
        scale: [0.5, 1, 1.6]
      }} transition={{
        duration: 4.5,
        repeat: Infinity,
        ease: 'easeOut',
        delay: 4.5
      }} data-id="element-227" />
        <motion.div className="absolute bottom-[22%] right-[16%] w-14 h-36 bg-[#FFF0F5]/30 blur-2xl rounded-full" animate={{
        y: [0, -130, -260],
        x: [0, 18, -5],
        opacity: [0, 0.4, 0],
        scale: [0.7, 1.3, 2]
      }} transition={{
        duration: 7,
        repeat: Infinity,
        ease: 'easeOut',
        delay: 2
      }} data-id="element-228" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-229">
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-230">
          <motion.div className="flex-1 text-center md:text-left" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-231">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 bg-gradient-to-r from-[#8B2252] via-[#C41E3A] to-[#8B2252] bg-clip-text text-transparent drop-shadow-lg" data-id="element-232">
              DESCONEXIÓN EMOCIONAL
            </h1>
            <h2 className="text-xl md:text-2xl text-terracotta font-medium italic mb-6" data-id="element-233">
              Por qué el amor se apaga aunque todo parezca bien
            </h2>

            <div className="space-y-4 text-lg md:text-xl text-mutedBrown leading-relaxed mb-8" data-id="element-234">
              <p data-id="element-235">
                La guía íntima y práctica para mujeres que se sienten solas
                dentro de su relación y necesitan entender qué se apagó,
                recuperar la conexión emocional o ganar la claridad necesaria
                para decidir sin culpa, sin impulsividad y sin seguir
                desapareciendo dentro de su propio hogar.
              </p>
              <p className="font-medium text-warmBrown" data-id="element-236">
                No quieres destruir tu familia. Pero tampoco quieres seguir
                viviendo sintiéndote sola, invisible y distante dentro de tu
                propia relación.
              </p>
            </div>

            <CTAButton text="QUIERO ENTENDER QUÉ ESTÁ PASANDO" microcopy="Acceso inmediato · Lectura privada · Hecha para mujeres que necesitan claridad emocional real" className="md:items-start" icon={<SearchIcon className="w-5 h-5" data-id="element-238" />} data-id="element-237" />
          </motion.div>

          <motion.div className="flex-1 w-full max-w-md mx-auto md:max-w-none" initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-239">
            <div className="relative" data-id="element-240">
              <div className="absolute inset-0 bg-softGold/20 blur-3xl rounded-full transform scale-90 translate-y-10" data-id="element-241"></div>
              <div className="absolute -top-4 -right-4 bg-[#C41E3A] text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg rotate-3 z-20" data-id="element-242">
                Oferta por Lanzamiento
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/wTK44LUP9MpFhkSSfKiSFh/Cover_de_Ebook_Landing.png" alt="Portada del libro Desconexión Emocional" className="relative z-10 w-full h-auto drop-shadow-2xl rounded-2xl overflow-hidden" data-id="element-243" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}